<!--
ScheduledJobFormModal — create/edit a scheduled_jobs row (S10 Full-only
console). Registered centrally in ModalManager.vue (forced — see that file's
S10 comment) and opened via eventBus 'modal.scheduledJobForm.open', optionally
with { row } for edit.

`channel` is NOT a free picker: ChannelRegistry (backend) only resolves
'email' — 'none' exists in the DDL/contract but is a guaranteed
`channel_not_available:none` failure for a faturamento_report job, and is
simply unused (ignored) by an internal_task job. Offering both as a live
choice would be a footgun that contradicts this whole domain's
fail-visibly philosophy, so the form DERIVES channel from job_type instead
of asking: 'faturamento_report' → 'email', every other type → 'none'.

`subscription_sweep` (S12/#17b — the end-of-carência sweep as an administrable
job) takes NO params and delivers nothing, so it shows no extra field: the
backend executor fails the run closed on any unexpected params key rather than
ignoring it, which is why this form must not invent one.

`params` (franquia_id/period for faturamento_report, task for internal_task)
is job-type-specific shape the backend treats as hostile input regardless of
what this form allowed through (ScheduledJobs_SaveInput docblock) — this
modal still keeps the two shapes tightly typed here so staff cannot type
free JSON.
-->
<template>
  <modal
    elem-id="modal_scheduled_job_form"
    :title="isEdit ? t('Editar job') : t('Novo job')"
    :show="isVisible"
    size="medium"
    :close-label="t('Fechar')"
    :save-label="t('Salvar')"
    :save-fn="handleSave"
    :save-disabled-fn="() => isSaving || isFormInvalid()"
    save-test-id="scheduled-job-form-submit"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <template v-if="isVisible">
      <div class="scheduled-job-form-fields">
        <input-text
          v-model="name"
          elem-id="scheduled-job-form-name"
          :label="t('Nome')"
          :ng-required="true"
          data-testid="scheduled-job-form-name-input"
        />

        <select-list
          v-model="jobType"
          elem-id="scheduled-job-form-job-type"
          :label="t('Tipo de job')"
          :itens-select="jobTypeOptions"
          :auto-select-first="false"
          :ng-required="true"
          data-testid="scheduled-job-form-job-type-select"
        />

        <input-toggle
          v-model="enabled"
          elem-id="scheduled-job-form-enabled"
          :label="t('Ativo')"
          data-testid="scheduled-job-form-enabled-toggle"
        />

        <select-list
          v-model="scheduleKind"
          elem-id="scheduled-job-form-schedule-kind"
          :label="t('Agenda')"
          :itens-select="scheduleKindOptions"
          :auto-select-first="false"
          :ng-required="true"
          data-testid="scheduled-job-form-schedule-kind-select"
        />

        <input-text
          v-if="scheduleKind === 'daily'"
          v-model="scheduleTime"
          elem-id="scheduled-job-form-schedule-time"
          :label="t('Horário (HH:MM)')"
          :placeholder="t('08:00')"
          :ng-required="true"
          data-testid="scheduled-job-form-schedule-time-input"
        />

        <input-number
          v-if="scheduleKind === 'interval'"
          v-model="intervalMinutes"
          elem-id="scheduled-job-form-interval-minutes"
          :label="t('Intervalo (minutos)') + ' (' + t('mínimo {min} — o despachante roda a cada {min} min', { min: MIN_INTERVAL_MINUTES }) + ')'"
          :min="MIN_INTERVAL_MINUTES"
          :ng-required="true"
          data-testid="scheduled-job-form-interval-minutes-input"
        />

        <!-- faturamento_report-only params -->
        <template v-if="jobType === 'faturamento_report'">
          <franchise-select
            v-model="franquiaId"
            elem-id="scheduled-job-form-franquia"
            :label="t('Franquia')"
            data-testid="scheduled-job-form-franquia-select"
          />
          <select-list
            v-model="period"
            elem-id="scheduled-job-form-period"
            :label="t('Período do relatório')"
            :itens-select="periodOptions"
            :auto-select-first="false"
            :ng-required="true"
            data-testid="scheduled-job-form-period-select"
          />
          <input-text
            v-model="channelTarget"
            elem-id="scheduled-job-form-channel-target"
            :label="t('E-mail de destino') + ' (' + t('opcional — usa o e-mail da franquia') + ')'"
            data-testid="scheduled-job-form-channel-target-input"
          />
        </template>

        <!-- internal_task-only params -->
        <select-list
          v-if="jobType === 'internal_task'"
          v-model="task"
          elem-id="scheduled-job-form-task"
          :label="t('Tarefa')"
          :itens-select="taskOptions"
          :auto-select-first="false"
          :ng-required="true"
          data-testid="scheduled-job-form-task-select"
        />

        <input-number
          v-model="maxAttempts"
          elem-id="scheduled-job-form-max-attempts"
          :label="t('Tentativas máximas')"
          :min="1"
          :ng-required="true"
          data-testid="scheduled-job-form-max-attempts-input"
        />
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import { useScheduledJobs } from '../../../composables/useScheduledJobs'
import type { ScheduledJobs_JobRow } from '../../../contracts/ScheduledJobs_JobRow'

const { t } = useI18n()
const { saveJob } = useScheduledJobs()

type JobType = 'faturamento_report' | 'internal_task' | 'subscription_sweep' | 'overdue_detection' | 'grace_reminder' | ''
type ScheduleKind = 'daily' | 'interval' | ''
type Period = 'day' | 'week' | 'month'
type Task = 'subscription_sweep' | 'credit_purchase_sweep' | ''

const HH_MM = /^([01]\d|2[0-3]):([0-5]\d)$/
// Mirrors ScheduledJobs_SaveInput::MIN_INTERVAL_MINUTES (backend, source of
// truth) — same value as the dispatcher's recommended cron tick period
// (docker-compose `*/5 * * * *`). Kept here only for client-side UX (min +
// help text); the backend rejects independently either way (S10-02 fix).
const MIN_INTERVAL_MINUTES = 5

const isVisible = ref(false)
const isSaving = ref(false)

const id = ref<number | null>(null)
const name = ref('')
const jobType = ref<JobType>('')
const enabled = ref(true)
const scheduleKind = ref<ScheduleKind>('')
const scheduleTime = ref('')
const intervalMinutes = ref<number | null>(null)
const maxAttempts = ref<number | null>(3)
const channelTarget = ref('')
// faturamento_report-only
const franquiaId = ref<number | string | null>(null)
const period = ref<Period>('week')
// internal_task-only
const task = ref<Task>('')

const isEdit = computed(() => id.value !== null)

// Derived, not staff-picked — see file docblock. Only faturamento_report
// delivers anything; every other type is channel-less.
// grace_reminder (M6) also delivers — countdown e-mail per franchise in grace.
const channel = computed<'email' | 'none'>(() =>
  (jobType.value === 'faturamento_report' || jobType.value === 'grace_reminder' ? 'email' : 'none'))

const jobTypeOptions = [
  { ID: 'faturamento_report', Name: t('Relatório de faturamento') },
  { ID: 'internal_task', Name: t('Tarefa interna') },
  { ID: 'subscription_sweep', Name: t('Sweep de assinaturas (fim de carência)') },
  { ID: 'overdue_detection', Name: t('Detecção de vencimento (sem webhook)') },
  { ID: 'grace_reminder', Name: t('Lembrete de carência (countdown)') },
]

const scheduleKindOptions = [
  { ID: 'daily', Name: t('Diário (horário fixo)') },
  { ID: 'interval', Name: t('Intervalo (x vezes por dia)') },
]

const periodOptions = [
  { ID: 'day', Name: t('Dia') },
  { ID: 'week', Name: t('Semana') },
  { ID: 'month', Name: t('Mês') },
]

const taskOptions = [
  { ID: 'subscription_sweep', Name: t('Sweep de assinaturas') },
  { ID: 'credit_purchase_sweep', Name: t('Sweep de compra de créditos') },
]

function isFormInvalid(): boolean {
  if (name.value.trim() === '') return true
  if (jobType.value === '') return true
  if (scheduleKind.value === '') return true
  if (scheduleKind.value === 'daily' && !HH_MM.test(scheduleTime.value)) return true
  if (scheduleKind.value === 'interval' && (!intervalMinutes.value || intervalMinutes.value < MIN_INTERVAL_MINUTES)) return true
  if (!maxAttempts.value || maxAttempts.value < 1) return true
  if (jobType.value === 'faturamento_report' && !franquiaId.value) return true
  if (jobType.value === 'internal_task' && !task.value) return true
  return false
}

function buildParams(): string | null {
  if (jobType.value === 'faturamento_report') {
    return JSON.stringify({ franquia_id: Number(franquiaId.value), period: period.value })
  }
  if (jobType.value === 'internal_task') {
    return JSON.stringify({ task: task.value })
  }
  return null
}

/**
 * Reverse of buildParams() for the edit path. `params` is staff-editable
 * text (hostile input, per design doc) — a malformed or job-type-mismatched
 * value never throws here, it just leaves the field(s) empty for staff to
 * refill, logged rather than swallowed silently.
 */
function parseParams(row: ScheduledJobs_JobRow): void {
  let parsed: Record<string, unknown> = {}
  if (row.params) {
    try {
      const decoded = JSON.parse(row.params)
      if (decoded && typeof decoded === 'object') {
        parsed = decoded as Record<string, unknown>
      }
    } catch (err) {
      log('ScheduledJobFormModal', 'parseParams', 'params inválido ao abrir edição — campos ficam vazios', {
        id: row.id,
        message: (err as Error)?.message,
      })
    }
  }

  if (row.job_type === 'faturamento_report') {
    const fid = parsed.franquia_id
    franquiaId.value = typeof fid === 'number' ? fid : (typeof fid === 'string' && fid !== '' ? fid : null)
    const p = parsed.period
    period.value = p === 'day' || p === 'week' || p === 'month' ? p : 'week'
  } else if (row.job_type === 'internal_task') {
    const tk = parsed.task
    task.value = tk === 'subscription_sweep' || tk === 'credit_purchase_sweep' ? tk : ''
  }
}

function resetFields(): void {
  id.value = null
  name.value = ''
  jobType.value = ''
  enabled.value = true
  scheduleKind.value = ''
  scheduleTime.value = ''
  intervalMinutes.value = null
  maxAttempts.value = 3
  channelTarget.value = ''
  franquiaId.value = null
  period.value = 'week'
  task.value = ''
}

function openModal(payload?: { row: ScheduledJobs_JobRow }): void {
  log('ScheduledJobFormModal', 'openModal', 'Abrindo modal de job', { edit: !!payload?.row })
  resetFields()
  const row = payload?.row
  if (row) {
    id.value = row.id
    name.value = row.name ?? ''
    jobType.value = (row.job_type as JobType) ?? ''
    enabled.value = row.enabled ?? false
    scheduleKind.value = (row.schedule_kind as ScheduleKind) ?? ''
    scheduleTime.value = row.schedule_time ?? ''
    intervalMinutes.value = row.interval_minutes ?? null
    maxAttempts.value = row.max_attempts ?? 3
    channelTarget.value = row.channel_target ?? ''
    parseParams(row)
  }
  isVisible.value = true
}

function closeModal(): void {
  isVisible.value = false
  resetFields()
}

async function handleSave(): Promise<void> {
  if (isFormInvalid()) return
  isSaving.value = true
  try {
    const paramsJson = buildParams()
    const row = await saveJob({
      ...(id.value != null ? { id: id.value } : {}),
      name: name.value.trim(),
      job_type: jobType.value as string,
      enabled: enabled.value,
      schedule_kind: scheduleKind.value as string,
      ...(scheduleKind.value === 'daily' ? { schedule_time: scheduleTime.value } : {}),
      ...(scheduleKind.value === 'interval' && intervalMinutes.value != null ? { interval_minutes: intervalMinutes.value } : {}),
      ...(paramsJson != null ? { params: paramsJson } : {}),
      channel: channel.value,
      ...(channel.value === 'email' && channelTarget.value.trim() !== '' ? { channel_target: channelTarget.value.trim() } : {}),
      max_attempts: maxAttempts.value ?? 3,
    })
    log('ScheduledJobFormModal', 'handleSave', 'Job salvo', { id: row.id })
    eventBus.emit('globalMessage.success', t('Job salvo com sucesso'))
    eventBus.emit('scheduledJobForm.saved', { row })
    closeModal()
  } catch (err) {
    log('ScheduledJobFormModal', 'handleSave', 'Erro ao salvar job', err)
    eventBus.emit('globalMessage.error', t('Erro ao salvar job'))
  } finally {
    isSaving.value = false
  }
}

onMounted(() => {
  eventBus.on('modal.scheduledJobForm.open', openModal)
})

onBeforeUnmount(() => {
  eventBus.off('modal.scheduledJobForm.open', openModal)
})
</script>

<style scoped>
.scheduled-job-form-fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
</style>
